import { Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Metadata } from '@redwoodjs/web';
import { RiAddLine } from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import Money from 'shared/utils/Money';
import Link from 'src/components/atoms/Link';
import StatusBadge from 'src/components/atoms/StatusBadge';
import { PageBody } from 'src/components/containers/PageBody';
import { PageHeader, PageType } from 'src/components/containers/PageHeader';
import { AgButton } from 'src/components/ui';
import useGridExport from 'src/hooks/useGridExport';
import { appLinks } from 'src/lib/routing';
import { createViewConfigStore, VIEW_PAGES } from 'src/managers/viewConfig.store';
import { DataGrid, GridColObj, GridToolbarQuickFilter, GridToolbarV2, useGridRows } from 'src/modules/grids';
import { StoreStateProvider } from 'src/providers/StoreStateProvider';
import { dateCellFormatter, dateGetter, formatCurrency } from 'src/utils/grid/cell/cellFormatters';

import useCustomerPortalApi from '../useCustomerPortalApi';

import { useCustomerPortalPurchaseOrdersGrid } from './useCustomerPortalPurchaseOrdersGrid';

const STORAGE_KEY = 'customer-purchase-orders';

function OrderLinkCell({ slug }: { slug: string }) {
  return <Link {...appLinks.editCustomerPortalPurchaseOrder({ slug })} label={`PO #${slug}`} />;
}

function InvoiceLinkCell({ slug }: { slug: string }) {
  return <Link {...appLinks.viewCustomerPortalInvoice({ slug })} label={`#${slug}`} />;
}

const columns: GridColObj = {
  slug: { headerName: 'Order #', flex: 1, renderCell: ({ value }) => <OrderLinkCell slug={value} /> },
  orderDate: {
    headerName: 'Date',
    flex: 1,
    type: 'date',
    valueGetter: (value) => dateGetter(value),
    valueFormatter: dateCellFormatter,
  },
  status: {
    headerName: 'Status',
    flex: 1,
    minWidth: 110,
    getFilterSelectOptions: [
      { value: 'DRAFT_CUSTOMER', label: 'Draft' },
      { value: 'PENDING', label: 'Pending' },
      { value: 'PICKING', label: 'Approved' },
      { value: 'SHIPPED', label: 'Shipped' },
      { value: 'CLOSED', label: 'Closed' },
      { value: 'CANCELLED', label: 'Cancelled' },
    ],
    valueGetter: (value) => (value === 'DRAFT_CUSTOMER' ? 'DRAFT' : value),
    renderCell: ({ value, row }) => <StatusBadge status={value} context="salesOrder" />,
  },
  destination: { headerName: 'Destination', flex: 1 },
  totalUnits: { headerName: 'Total Units', flex: 1, type: 'number' },
  totalPrice: {
    headerName: 'Total Price',
    flex: 1,
    type: 'number',
    serverFilterValueTransform: { to: (value) => Money.toCents(value), from: (value) => Money.toDollars(value) },
    valueFormatter: (value, row) =>
      typeof value === 'number'
        ? formatCurrency({ value: Money.toDollars(value), currency: row.currencyCode || 'USD' })
        : '--',
  },
  invoiceSlug: { headerName: 'Linked Invoice', flex: 1, renderCell: ({ value }) => <InvoiceLinkCell slug={value} /> },
};

const gridQuickFilters: GridToolbarQuickFilter[] = [
  { label: 'All', filters: [{ field: 'status', where: [{ not: 'DRAFT' }] }] },
  { label: 'Draft', filters: [{ field: 'status', where: [{ equals: 'DRAFT_CUSTOMER' }] }] },
  { label: 'Pending', filters: [{ field: 'status', where: [{ equals: 'PENDING' }] }] },
  { label: 'Approved', filters: [{ field: 'status', where: [{ equals: 'PICKING' }] }] },
  { label: 'Shipped', filters: [{ field: 'status', where: [{ equals: 'SHIPPED' }] }] },
  { label: 'Closed', filters: [{ field: 'status', where: [{ equals: 'CLOSED' }] }] },
  { label: 'Cancelled', filters: [{ field: 'status', where: [{ equals: 'CANCELLED' }] }] },
];

export default function CustomerPortalPurchaseOrdersPage() {
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const enhancedGridState = useGridRows({
    mode: 'paged',
    storageKey: STORAGE_KEY,
    quickFilterOptions: gridQuickFilters,
  });
  const { rows, loading, pageInfo, totalCount, fetchAllForExport } =
    useCustomerPortalPurchaseOrdersGrid(enhancedGridState);
  const { createPurchaseOrder } = useCustomerPortalApi();

  useGridExport(apiRef, 'purchase-orders', undefined, undefined, fetchAllForExport);

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        quickFilterCountModel="CustomerPortalPurchaseOrder"
        viewPage={VIEW_PAGES.CUSTOMER_PORTAL_PUCHASE_ORDERS}
      />
    ),
    [apiRef, enhancedGridState]
  );

  const handleAddOrder = async () => {
    const slug = await createPurchaseOrder();
    await navigate(appLinks.editCustomerPortalPurchaseOrder({ slug }));
  };

  return (
    <>
      <Metadata title="Orders" />
      <PageHeader pageType={PageType.CustomerPortalPurchaseOrders}>
        <Box sx={{ ml: 'auto', display: 'flex', my: 'auto', gap: 1 }}>
          <AgButton variant="primary" onPress={handleAddOrder}>
            <RiAddLine size={20} />
            Add New Order
          </AgButton>
        </Box>
      </PageHeader>
      <PageBody>
        <StoreStateProvider initializeStore={createViewConfigStore(VIEW_PAGES.CUSTOMER_PORTAL_PUCHASE_ORDERS)}>
          <DataGrid
            mode="paged"
            enhancedGridState={enhancedGridState}
            totalCount={totalCount}
            pageInfo={pageInfo}
            loading={loading}
            rows={rows}
            gridKey="customer-portal.purchase-orders.list"
            storageKey={STORAGE_KEY}
            columns={columns}
            apiRef={apiRef}
            slots={{ toolbar }}
          />
        </StoreStateProvider>
      </PageBody>
    </>
  );
}
